import Phaser from 'phaser';
import type { BoardGeometry } from '../config/BoardGeometry';
import { getGameplayColorHex } from '../config/colors';
import { darkenHex, lightenHex } from '../utils/color';

/**
 * Renders a single snake column as a unified body graphic.
 * Drawn as a tall rounded rectangle that spans from topRow to bottomRow,
 * with a darker border, subtle scale pattern, and eye indicator at the top.
 * Shrinks with juicy animation when bottom cubes are cleared.
 */
export class SnakeOverlay extends Phaser.GameObjects.Container {
  private col: number;
  private topRow: number;
  private bottomRow: number;
  private colorIndex: number;
  private geometry: BoardGeometry;
  private bodyGraphics: Phaser.GameObjects.Graphics;
  private eyeGraphics: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    col: number,
    topRow: number,
    bottomRow: number,
    colorIndex: number,
    geometry: BoardGeometry,
  ) {
    super(scene, 0, 0);
    this.col = col;
    this.topRow = topRow;
    this.bottomRow = bottomRow;
    this.colorIndex = colorIndex;
    this.geometry = geometry;

    this.bodyGraphics = scene.add.graphics();
    this.eyeGraphics = scene.add.graphics();
    this.add(this.bodyGraphics);
    this.add(this.eyeGraphics);

    scene.add.existing(this as Phaser.GameObjects.GameObject);
    this.setDepth(5); // Above cubes but below projectiles

    this.redraw();
  }

  /** The number of cells this snake currently spans. */
  get cellCount(): number {
    return this.bottomRow - this.topRow + 1;
  }

  /**
   * Called when the bottom cell of this snake is cleared.
   * Animates the snake shrinking upward by one cell.
   * Returns true if the snake still has cells; false if fully destroyed.
   */
  shrinkFromBottom(): boolean {
    if (this.bottomRow <= this.topRow) {
      // Last cell — pop animation and destroy
      this.popAndDestroy();
      return false;
    }

    const oldBottomRow = this.bottomRow;
    this.bottomRow--;

    // Redraw immediately at the new size
    this.redraw();

    // Flash the body briefly
    this.bodyGraphics.setAlpha(0.7);
    this.scene.tweens.add({
      targets: this.bodyGraphics,
      alpha: 1,
      duration: 150,
      ease: 'Sine.easeOut',
    });

    // Particle burst at the cleared cell position
    const { offsetX, offsetY, cellSize } = this.geometry;
    const clearX = offsetX + this.col * cellSize + cellSize / 2;
    const clearY = offsetY + oldBottomRow * cellSize + cellSize / 2;
    const tint = getGameplayColorHex(this.colorIndex);

    const emitter = this.scene.add.particles(clearX, clearY, 'particle', {
      speed: { min: 20, max: 60 },
      scale: { start: 0.6, end: 0 },
      alpha: { start: 0.8, end: 0 },
      lifespan: 250,
      tint,
      emitting: false,
    });
    emitter.explode(4);
    this.scene.time.delayedCall(300, () => emitter.destroy());

    // Brief jiggle on the body graphics for juiciness
    const origY = this.bodyGraphics.y;
    this.scene.tweens.add({
      targets: this.bodyGraphics,
      y: origY + 3,
      duration: 60,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    return true;
  }

  /** Pop animation when the last cell is cleared. */
  private popAndDestroy(): void {
    const { offsetX, offsetY, cellSize } = this.geometry;
    const clearX = offsetX + this.col * cellSize + cellSize / 2;
    const clearY = offsetY + this.topRow * cellSize + cellSize / 2;
    const tint = getGameplayColorHex(this.colorIndex);

    // Particle burst
    const emitter = this.scene.add.particles(clearX, clearY, 'particle', {
      speed: { min: 40, max: 100 },
      scale: { start: 1.0, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: 350,
      tint,
      emitting: false,
    });
    emitter.explode(8);
    this.scene.time.delayedCall(400, () => emitter.destroy());

    // Scale up + fade out
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.6,
      scaleY: 1.6,
      alpha: 0,
      duration: 200,
      ease: 'Back.easeIn',
      onComplete: () => {
        this.bodyGraphics.destroy();
        this.eyeGraphics.destroy();
        this.destroy();
      },
    });
  }

  /** Redraw the snake body from scratch based on current topRow/bottomRow. */
  private redraw(): void {
    const g = this.bodyGraphics;
    const e = this.eyeGraphics;
    g.clear();
    e.clear();

    const hex = getGameplayColorHex(this.colorIndex);

    const { offsetX, offsetY, cellSize } = this.geometry;
    const x = offsetX + this.col * cellSize;
    const y = offsetY + this.topRow * cellSize;
    const w = cellSize;
    const h = (this.bottomRow - this.topRow + 1) * cellSize;
    const r = Math.min(4, cellSize / 4); // corner radius

    // === Main body fill (slightly darker/richer than normal cubes) ===
    const bodyColor = darkenHex(hex, 0.10);
    g.fillStyle(bodyColor, 1);
    g.fillRoundedRect(x + 1, y + 1, w - 2, h - 2, r);

    // === Gradient-like effect: lighter strip at top, darker at bottom ===
    const highlightColor = lightenHex(hex, 0.20);
    g.fillStyle(highlightColor, 0.3);
    g.fillRoundedRect(x + 2, y + 2, w - 4, Math.min(h * 0.3, cellSize), r);

    const shadowColor = darkenHex(hex, 0.30);
    g.fillStyle(shadowColor, 0.25);
    g.fillRoundedRect(x + 2, y + h - Math.min(h * 0.25, cellSize) - 1, w - 4, Math.min(h * 0.25, cellSize), r);

    // === Scale pattern (subtle diamond/chevron marks) ===
    g.lineStyle(1, darkenHex(hex, 0.15), 0.3);
    const patternStep = cellSize;
    for (let py = y + patternStep; py < y + h - 2; py += patternStep) {
      // Horizontal segment lines between cells
      g.beginPath();
      g.moveTo(x + 3, py);
      g.lineTo(x + w - 3, py);
      g.strokePath();

      // Small chevron/V marks for scale texture
      const midX = x + w / 2;
      g.beginPath();
      g.moveTo(midX - 4, py - 3);
      g.lineTo(midX, py + 1);
      g.lineTo(midX + 4, py - 3);
      g.strokePath();
    }

    // === Border (darker shade of same color, prominent) ===
    const borderColor = darkenHex(hex, 0.35);
    g.lineStyle(3, borderColor, 0.9);
    g.strokeRoundedRect(x + 1, y + 1, w - 2, h - 2, r);

    // === Tail indicator at bottom (rounded bump) ===
    if (h > cellSize) {
      const tailY = y + h - 4;
      const tailMidX = x + w / 2;
      g.lineStyle(2, borderColor, 0.5);
      g.beginPath();
      g.moveTo(tailMidX - 4, tailY);
      g.lineTo(tailMidX, tailY + 3);
      g.lineTo(tailMidX + 4, tailY);
      g.strokePath();
    }

    // === Eyes at the top cell ===
    this.drawEyes(e, x, y, w);
  }

  /** Draw two small eyes at the top of the snake. */
  private drawEyes(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number): void {
    const { cellSize } = this.geometry;
    const eyeY = y + cellSize * 0.35;
    const eyeSize = Math.max(2.4, cellSize * 0.15);
    const eyeSpacing = w * 0.28;
    const midX = x + w / 2;

    // White eye background
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(midX - eyeSpacing, eyeY, eyeSize);
    g.fillCircle(midX + eyeSpacing, eyeY, eyeSize);

    // Dark pupil
    g.fillStyle(0x222222, 1);
    g.fillCircle(midX - eyeSpacing, eyeY + 0.6, eyeSize * 0.55);
    g.fillCircle(midX + eyeSpacing, eyeY + 0.6, eyeSize * 0.55);
  }

}
